/**
 * Keycloak service
 * - Production/Staging: connects to real Keycloak instance
 * - Development: falls back to mock auth if Keycloak unreachable
 */
import Keycloak from 'keycloak-js'
import { useAuthStore } from '@/store/auth.js'

const useMock = import.meta.env.VITE_USE_MOCK === 'true'

let keycloakInstance = null

export async function initKeycloak() {
  const auth = useAuthStore()

  const kcConfig = {
    url: import.meta.env.VITE_KEYCLOAK_URL,
    realm: import.meta.env.VITE_KEYCLOAK_REALM,
    clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID
  }

  keycloakInstance = new Keycloak(kcConfig)

  const authenticated = await keycloakInstance.init({
    onLoad: 'check-sso',
    silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
    pkceMethod: 'S256'
  })

  if (authenticated) {
    auth.setUser({
      id: keycloakInstance.subject,
      name: keycloakInstance.tokenParsed?.name || keycloakInstance.tokenParsed?.preferred_username,
      email: keycloakInstance.tokenParsed?.email,
      token: keycloakInstance.token
    })
  }

  // Token refresh every 60s
  setInterval(async () => {
    try {
      const refreshed = await keycloakInstance.updateToken(70)
      if (refreshed) auth.setToken(keycloakInstance.token)
    } catch {
      auth.logout()
    }
  }, 60000)
}

export function loginWithKeycloak(redirectUri) {
  if (keycloakInstance) {
    keycloakInstance.login({ redirectUri: redirectUri || window.location.origin + '/dashboard' })
  } else if (useMock) {
    // Mock login for dev without Keycloak
    useMockLogin()
  }
}

export function registerWithKeycloak(planId) {
  // Carry the chosen plan through the auth redirect so it can be applied
  // to the customer's account once they land back on the dashboard.
  const redirectUri = window.location.origin + '/dashboard' + (planId ? `?plan=${encodeURIComponent(planId)}` : '')
  if (keycloakInstance) {
    keycloakInstance.register({ redirectUri })
  } else if (useMock) {
    useMockLogin(planId)
  }
}

export function logoutFromKeycloak() {
  if (keycloakInstance && keycloakInstance.authenticated) {
    keycloakInstance.logout({ redirectUri: window.location.origin })
  } else {
    useAuthStore().logout()
    window.location.href = '/'
  }
}

function useMockLogin(planId) {
  const auth = useAuthStore()
  auth.setUser({
    id: 'mock-user-001',
    name: 'Demo User',
    email: 'demo@custus.io',
    token: 'mock-token'
  })
  window.location.href = '/dashboard' + (planId ? `?plan=${encodeURIComponent(planId)}` : '')
}

export { keycloakInstance }
